import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyGetPlannerMetricsExportQuery } from '@store/api/eventService/eventServiceApi';
import { useGlobalToast } from '@/hooks/useGlobalToast';
import { downloadBlob } from '@/utils/downloadBlob';
import { getLocalizationConfig } from '@config/localizationConfig';
import { telemetry } from '@/services/telemetry';
import { TELEMETRY_COMPONENT } from '@/constants';
import type { MetricsPeriodInput } from '@store/models';

/** Options for {@link usePlannerMetricsExport}. */
export interface UsePlannerMetricsExportOptions {
  /** Selected building filter (empty means all buildings). */
  buildingIds: string[];
  /** The primary reporting period whose start/end date scope the export. */
  period: MetricsPeriodInput | undefined;
}

/** Public shape returned by {@link usePlannerMetricsExport}. */
export interface UsePlannerMetricsExportResult {
  /** Triggers the server-side export and browser download. */
  exportMetrics: () => Promise<void>;
  /** True while an export request is in flight. */
  isExporting: boolean;
  /** True when a valid primary date range is present (Export is enabled). */
  canExport: boolean;
}

function isPeriodComplete(period: MetricsPeriodInput | undefined): boolean {
  if (!period) return false;
  if (!period.startDate || !period.endDate) return false;
  return period.startDate <= period.endDate;
}

/**
 * Owns the planner metrics export flow: validates the selected date range,
 * triggers the server-side `GET /events/metrics/export`, streams the returned
 * workbook to a browser download, prevents duplicate concurrent exports, and
 * emits success/failure telemetry (spec 116-planner-metrics-export, FR-016/FR-019).
 */
export function usePlannerMetricsExport({
  buildingIds,
  period,
}: UsePlannerMetricsExportOptions): UsePlannerMetricsExportResult {
  const { t } = useTranslation('metricsReport');
  const { showToast } = useGlobalToast();
  const [trigger] = useLazyGetPlannerMetricsExportQuery();
  const [isExporting, setIsExporting] = useState(false);
  const inFlightRef = useRef(false);

  const canExport = useMemo(() => isPeriodComplete(period), [period]);

  const exportMetrics = useCallback(async () => {
    if (inFlightRef.current || !isPeriodComplete(period)) return;

    const dateFormat = getLocalizationConfig().dateFormatPattern;
    const params = {
      ...(buildingIds.length > 0 ? { buildingIds } : {}),
      startDate: period!.startDate,
      endDate: period!.endDate,
      dateFormat,
    };

    inFlightRef.current = true;
    setIsExporting(true);
    try {
      const result = await trigger(params).unwrap();
      downloadBlob(result.blob, result.filename);
      telemetry.trackSuccess(TELEMETRY_COMPONENT, 'PlannerMetricsExport', {
        buildingCount: String(buildingIds.length),
      });
    } catch {
      telemetry.trackFailure(TELEMETRY_COMPONENT, 'PlannerMetricsExport', 'export-failed');
      showToast({ message: t('export.failed'), intent: 'error' });
    } finally {
      inFlightRef.current = false;
      setIsExporting(false);
    }
  }, [buildingIds, period, trigger, showToast, t]);

  return { exportMetrics, isExporting, canExport };
}
